/**
 * Offline Authentication Configuration
 *
 * SECURITY WARNING:
 * This configuration is for DEMO and INTERNAL USE ONLY.
 * Hardcoded credentials should NEVER be used in production environments.
 *
 * This file enables offline presentation access for:
 * - Product demos when backend is unavailable
 * - Internal development and testing
 * - Emergency fallback during backend outages
 *
 * Environment Variables:
 * - VITE_OFFLINE_MODE: Set to 'true' to enable offline authentication
 * - VITE_OFFLINE_SUPER_ADMIN_EMAIL: Override default offline super admin email
 * - VITE_OFFLINE_SUPER_ADMIN_PASSWORD: Override default offline super admin password
 */

/**
 * User role types
 */
export type UserRole = 'demo' | 'company_user' | 'company_admin' | 'super_admin'

/**
 * User interface (matches localStorage user structure)
 */
export interface User {
  id: string
  email: string
  name: string
  role: UserRole
  companyId?: string
}

/**
 * Offline mode configuration
 */
export interface OfflineAuthConfig {
  enabled: boolean
  superAdmin: OfflineCredentials
  warningMessage: string
}

/**
 * Offline credentials for demo/internal use
 */
export interface OfflineCredentials {
  email: string
  password: string
  user: User
}

/**
 * Default offline super admin credentials
 * WARNING: CREDENTIALS REMOVED FOR SECURITY
 * To enable offline mode, set environment variables:
 * - VITE_OFFLINE_SUPER_ADMIN_EMAIL
 * - VITE_OFFLINE_SUPER_ADMIN_PASSWORD
 */
const DEFAULT_OFFLINE_SUPER_ADMIN: OfflineCredentials = {
  email: '',
  password: '',
  user: {
    id: 'offline-super-admin-001',
    email: '',
    name: 'Offline Demo Admin',
    role: 'super_admin',
    companyId: undefined,
  },
}

/**
 * Check if offline mode is enabled via environment variable
 * NOTE: Offline mode is always enabled for demo purposes.
 * Set VITE_OFFLINE_MODE=false to disable.
 */
function isOfflineModeEnabled(): boolean {
  return import.meta.env.VITE_OFFLINE_MODE !== 'false'
}

/**
 * Get offline super admin credentials from environment or defaults
 */
function getOfflineSuperAdmin(): OfflineCredentials {
  const email = import.meta.env.VITE_OFFLINE_SUPER_ADMIN_EMAIL || DEFAULT_OFFLINE_SUPER_ADMIN.email
  const password = import.meta.env.VITE_OFFLINE_SUPER_ADMIN_PASSWORD || DEFAULT_OFFLINE_SUPER_ADMIN.password

  return {
    email,
    password,
    user: {
      ...DEFAULT_OFFLINE_SUPER_ADMIN.user,
      email,
      name: `Offline Demo Admin (${email.split('@')[0]})`,
    },
  }
}

/**
 * Offline authentication configuration
 */
export const OFFLINE_AUTH_CONFIG: OfflineAuthConfig = {
  enabled: isOfflineModeEnabled(),
  superAdmin: getOfflineSuperAdmin(),
  warningMessage: "",
}

/**
 * Validate credentials against offline super admin
 *
 * @param email - User email
 * @param password - User password
 * @returns User object if credentials match, null otherwise
 */
export function validateOfflineCredentials(email: string, password: string): User | null {
  if (!OFFLINE_AUTH_CONFIG.enabled) {
    return null
  }

  const { superAdmin } = OFFLINE_AUTH_CONFIG

  if (email.toLowerCase() === superAdmin.email.toLowerCase() && password === superAdmin.password) {
    return superAdmin.user
  }

  return null
}

/**
 * Set offline authentication in localStorage
 *
 * @param user - User object to store
 */
export function setOfflineAuth(user: User): void {
  localStorage.setItem('user', JSON.stringify(user))
  localStorage.setItem('token', `offline-token-${user.id}`)
  localStorage.setItem('offline-mode', 'true')
}

/**
 * Check if currently authenticated via offline mode
 *
 * @returns true if offline mode is active
 */
export function isOfflineModeActive(): boolean {
  return localStorage.getItem('offline-mode') === 'true'
}

/**
 * Clear offline authentication from localStorage
 */
export function clearOfflineAuth(): void {
  localStorage.removeItem('user')
  localStorage.removeItem('token')
  localStorage.removeItem('offline-mode')
}

